import { PersonAssetGraphPageDTOSchema } from "./person-asset-graph.dto";
import { PolicyReviewRecordSchema, PolicyReviewReportSnapshotSchema } from './policy-review.dto';
import { ApiCheckExecutionDTOSchema } from './api-test-run.dto';
import { z } from "zod/v3";
import type { ControlApprovalDTO } from "./control-evidence-review.dto";
import { VendorDTOSchema, VendorDocumentDTOSchema, VendorAssessmentInputDTOSchema } from "./vendor.dto";
import { VendorAssessmentDTOSchema, VendorEvidenceRequestDTOSchema, VendorControlMappingDTOSchema } from "./vendor-assessment.dto";
import { KeyDocumentSchema, KeyDocumentsSchema } from "./audit-package.dto";
import { AssetDTOSchema } from "./asset.dto";
import { RiskAcceptanceDTOSchema, RiskItemDTOSchema, RiskRatingEnum, RiskStatusEnum, RiskTreatmentEnum } from "./risk-register.dto";
import { ComplianceExclusionReasonSchema, DeviceLifecycleStatusSchema } from "../generated/enums";

export const AuditCycleStatusSchema = z.enum([
  "planning", "active", "audit-ready", "in-audit", "completed",
]);
export type AuditCycleStatus = z.infer<typeof AuditCycleStatusSchema>;

export const AUDIT_CYCLE_STATUS_LABELS: Record<AuditCycleStatus, string> = {
  planning: "Planning",
  active: "Audit Preparation",
  "audit-ready": "Audit Ready",
  "in-audit": "In Audit",
  completed: "Completed",
};

export const AuditorCycleSummarySchema = z.object({
  id: z.string(),
  label: z.string(),
  auditType: z.string(),
  periodStart: z.date(),
  periodEnd: z.date(),
  status: z.string(),
  framework: z.string().nullable(),
  grantedAt: z.date(),
});

export type AuditorCycleSummary = z.infer<typeof AuditorCycleSummarySchema>;

export const AuditorCommentTargetSchema = z.enum([
  "CONTROL",
  "TEST",
  "EVIDENCE_ITEM",
  "POLICY",
  "POLICY_SUBMISSION",
  "GENERAL",
]);

export type AuditorCommentTarget = z.infer<typeof AuditorCommentTargetSchema>;

export const AuditorCommentTypeSchema = z.enum([
  "NOTE",
  "EVIDENCE_REQUEST",
  "CLARIFICATION",
  "EXCEPTION",
]);

export type AuditorCommentType = z.infer<typeof AuditorCommentTypeSchema>;

export const AuditorReviewStatusSchema = z.enum([
  "REVIEWED",
  "SATISFACTORY",
  "EXCEPTION_NOTED",
  "NEEDS_REMEDIATION",
]);

export type AuditorReviewStatus = z.infer<typeof AuditorReviewStatusSchema>;

/** An auditor's cycle-scoped conclusion, independent of company approval. */
export type AuditorControlReviewDTO = {
  id: string;
  status: Exclude<AuditorReviewStatus, "REVIEWED">;
  assessment: string;
  reviewerName: string | null;
  reviewedAt: Date;
  stale: boolean;
  /** A company approval after this remediation conclusion handed it back for review. */
  reReviewRequestedAt?: Date;
};

/** The current handoff stage; recorded auditor conclusions remain independent. */
export function getAuditorControlStatus(control: {
  approvalStage?: ControlApprovalDTO['stage'] | null;
  auditorReview?: Pick<AuditorControlReviewDTO, 'status' | 'stale' | 'reReviewRequestedAt'> | null;
}) {
  if (control.approvalStage === 'NOT_APPLICABLE') return 'OUT_OF_SCOPE' as const;
  if (control.auditorReview && !control.auditorReview.stale) return control.auditorReview.status;
  if (!control.approvalStage) return 'UNAVAILABLE' as const;
  return control.approvalStage === 'APPROVED' || control.approvalStage === 'LEGACY_ACCEPTED'
    ? 'PENDING_REVIEW' as const
    : 'PENDING_SUBMISSION' as const;
}

export type AuditorControlStatus = ReturnType<typeof getAuditorControlStatus>;

export const AuditorPolicySubmissionStatusSchema = z.enum([
  "SUBMITTED",
  "APPROVED",
  "NEEDS_REVISION",
  "SUPERSEDED",
]);

export type AuditorPolicySubmissionStatus = z.infer<
  typeof AuditorPolicySubmissionStatusSchema
>;

export const AuditorReadinessBreakdownSchema = z.object({
  overallScore: z.number().nullable(),
  policies: z.number().nullable(),
  technicalControls: z.number().nullable(),
  behavioralControls: z.number().nullable(),
  matrixCoverage: z.number().nullable(),
  evidenceFreshness: z.number().nullable(),
  capturedAt: z.date().nullable(),
});

export type AuditorReadinessBreakdown = z.infer<
  typeof AuditorReadinessBreakdownSchema
>;

export const AuditorDashboardStatsSchema = z.object({
  /** RFC-214 headline pair — counted with `buildSetupCanonicalTestWhere`, the
   *  same predicate the Tests page uses, so the two surfaces cannot drift. */
  totalTests: z.number(),
  completeTests: z.number(),
  totalControls: z.number(),
  completeControls: z.number(),
  inProgressControls: z.number(),
  pendingControls: z.number(),
  evidenceArtifacts: z.number(),
  auditPackages: z.number(),
  verifiedPolicies: z.number(),
  openRequests: z.number(),
  unresolvedComments: z.number(),
  employeeCount: z.number(),
  activeEmployeeCount: z.number(),
  trainingCompletionRate: z.number(),
  policyAcknowledgmentCoverage: z.number(),
  compliantDeviceCoverage: z.number(),
});

export type AuditorDashboardStats = z.infer<typeof AuditorDashboardStatsSchema>;

export const AuditorDashboardRecentActivitySchema = z.object({
  id: z.string(),
  eventType: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  createdAt: z.date(),
});

export type AuditorDashboardRecentActivity = z.infer<
  typeof AuditorDashboardRecentActivitySchema
>;

export const AuditorAuditPackageSummarySchema = z.object({
  id: z.string(),
  auditCycleId: z.string(),
  status: z.string(),
  fileName: z.string().nullable(),
  fileSize: z.number().nullable(),
  artifactCount: z.number().nullable(),
  manifestHash: z.string().nullable(),
  generatedBy: z.string(),
  errorMessage: z.string().nullable(),
  createdAt: z.date(),
  downloadUrl: z.string().nullable(),
});

export type AuditorAuditPackageSummary = z.infer<
  typeof AuditorAuditPackageSummarySchema
>;

export const AuditorDashboardDTOSchema = z.object({
  cycle: z.object({
    id: z.string(),
    label: z.string(),
    auditType: z.string(),
    periodStart: z.date(),
    periodEnd: z.date(),
    status: z.string(),
    targetAuditDate: z.date().nullable(),
    finalReadinessScore: z.number().nullable(),
    finalBlockerCount: z.number().nullable(),
    completedAt: z.date().nullable(),
    framework: z.string().nullable(),
    programId: z.string(),
  }),
  readiness: AuditorReadinessBreakdownSchema.nullable(),
  blockers: z.array(z.string()),
  warnings: z.array(z.string()),
  stats: AuditorDashboardStatsSchema,
  recentPackages: z.array(AuditorAuditPackageSummarySchema),
  recentActivity: z.array(AuditorDashboardRecentActivitySchema),
});

export type AuditorDashboardDTO = z.infer<typeof AuditorDashboardDTOSchema>;

export const AuditorCommentThreadSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    id: z.string(),
    auditCycleId: z.string(),
    targetType: AuditorCommentTargetSchema,
    targetId: z.string().nullable(),
    targetRef: z.string().nullable(),
    evidenceSubmissionId: z.string().nullable(),
    submissionRevisionNumber: z.number().nullable(),
    commentType: AuditorCommentTypeSchema,
    reviewStatus: AuditorReviewStatusSchema.nullable(),
    content: z.string(),
    isAuditor: z.boolean(),
    authorName: z.string().nullable(),
    authorEmail: z.string().nullable(),
    createdAt: z.date(),
    respondedAt: z.date().nullable(),
    respondedByName: z.string().nullable(),
    resolvedAt: z.date().nullable(),
    resolvedByName: z.string().nullable(),
    replies: z.array(AuditorCommentThreadSchema),
  }),
);

export type AuditorCommentThreadDTO = z.infer<
  typeof AuditorCommentThreadSchema
>;

export const AuditorCommentThreadPageSchema = z.object({
  items: z.array(AuditorCommentThreadSchema),
  totalCount: z.number(),
  nextCursor: z.string().nullable(),
});

export type AuditorCommentThreadPageDTO = z.infer<
  typeof AuditorCommentThreadPageSchema
>;

export const AuditorControlNodeSchema = z.object({
  controlId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  framework: z.string().nullable(),
  status: z.string().nullable(),
  isNotApplicable: z.boolean(),
  hasException: z.boolean(),
  exceptionDescription: z.string().nullable(),
  frequency: z.string().nullable(),
  nextDueDate: z.date().nullable(),
  lastSubmittedAt: z.date().nullable(),
  linkedEvidenceCount: z.number(),
  latestReviewStatus: AuditorReviewStatusSchema.nullable(),
  commentCount: z.number(),
  unresolvedRequestCount: z.number(),
  tests: z.array(
    z.object({
      statusId: z.string(),
      title: z.string(),
      templateType: z.string().nullable(),
      /** RFC-234 M3 — how the evidence is collected (see TestCollectionSchema). */
      collection: z.enum(['automated', 'policy_link', 'attestation', 'upload']).optional(),
      status: z.string().nullable(),
      reviewStatus: AuditorReviewStatusSchema.nullable(),
      lastSubmittedAt: z.date().nullable(),
      linkedEvidenceCount: z.number(),
      commentCount: z.number(),
      unresolvedRequestCount: z.number(),
    }),
  ),
});

export type AuditorControlNode = z.infer<typeof AuditorControlNodeSchema>;

export const AuditorControlsTreeGroupSchema = z.object({
  framework: z.string(),
  controls: z.array(AuditorControlNodeSchema),
});

export type AuditorControlsTreeGroup = z.infer<
  typeof AuditorControlsTreeGroupSchema
>;

export const AuditorControlsTreeDTOSchema = z.object({
  availableFrameworks: z.array(z.string()),
  groups: z.array(AuditorControlsTreeGroupSchema),
  totalCount: z.number(),
  nextCursor: z.string().nullable(),
  appliedFilters: z.record(z.string(), z.unknown()),
});

export type AuditorControlsTreeDTO = z.infer<
  typeof AuditorControlsTreeDTOSchema
>;

export const AuditorEvidenceItemSchema = z.object({
  id: z.string(),
  kind: z.enum(["package", "file", "submission"]),
  title: z.string(),
  subtitle: z.string().nullable(),
  controlId: z.string().nullable(),
  controlRef: z.string().nullable(),
  fileType: z.string().nullable(),
  fileSize: z.number().nullable(),
  reviewStatus: z.string().nullable(),
  submittedAt: z.date().nullable(),
  uploadedAt: z.date().nullable(),
  downloadUrl: z.string().nullable(),
  previewUrl: z.string().nullable(),
  canPreview: z.boolean(),
  canDownload: z.boolean(),
  hasLinkedFiles: z.boolean(),
  linkedFiles: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      fileType: z.string().nullable(),
      size: z.number().nullable(),
      uploadedAt: z.date().nullable(),
      downloadUrl: z.string().nullable(),
      previewUrl: z.string().nullable(),
    }),
  ),
});

export type AuditorEvidenceItem = z.infer<typeof AuditorEvidenceItemSchema>;

export const AuditorEvidenceGroupSchema = z.object({
  controlId: z.string(),
  controlTitle: z.string(),
  items: z.array(AuditorEvidenceItemSchema),
});

export type AuditorEvidenceGroup = z.infer<typeof AuditorEvidenceGroupSchema>;

export const AuditorEvidenceViewDTOSchema = z.object({
  packages: z.array(AuditorAuditPackageSummarySchema),
  groups: z.array(AuditorEvidenceGroupSchema),
  totalFiles: z.number(),
  totalSubmissions: z.number(),
  availableControls: z.array(z.string()),
  totalCount: z.number(),
  nextCursor: z.string().nullable(),
  appliedFilters: z.record(z.string(), z.unknown()),
});

export type AuditorEvidenceViewDTO = z.infer<
  typeof AuditorEvidenceViewDTOSchema
>;

export const AuditorAuditEvidenceItemSchema = z.object({
  policyReviewSnapshot: PolicyReviewReportSnapshotSchema.nullable().optional(),
  isPolicyReviewReport: z.boolean().optional(),
  id: z.string(),
  kind: z.enum(["file", "submission"]),
  title: z.string(),
  filename: z.string(),
  source: z.string(),
  freshness: z.enum(["fresh", "aging", "stale", "unknown"]),
  reviewStatus: z.string().nullable(),
  statusId: z.string(),
  testCode: z.string().nullable(),
  testTitle: z.string(),
  testStatus: z.string().nullable(),
  controlId: z.string(),
  controlRef: z.string(),
  controlTitle: z.string().nullable(),
  controlKey: z.string(),
  mappedControls: z.array(
    z.object({
      controlId: z.string(),
      controlTitle: z.string().nullable(),
      controlKey: z.string(),
    }),
  ),
  updatedAt: z.date().nullable(),
  submittedAt: z.date().nullable(),
  uploadedAt: z.date().nullable(),
  fileType: z.string().nullable(),
  fileSize: z.number().nullable(),
  previewUrl: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  executions: z.array(ApiCheckExecutionDTOSchema).nullable().optional(),
  canPreview: z.boolean(),
  canDownload: z.boolean(),
  linkedFiles: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      fileType: z.string().nullable(),
      uploadedAt: z.date().nullable(),
      downloadUrl: z.string().nullable(),
  executions: z.array(ApiCheckExecutionDTOSchema).nullable().optional(),
      previewUrl: z.string().nullable(),
    }),
  ),
});

export type AuditorAuditEvidenceItem = z.infer<
  typeof AuditorAuditEvidenceItemSchema
>;

export const AuditorAuditEvidencePageSchema = z.object({
  items: z.array(AuditorAuditEvidenceItemSchema),
  availableControls: z.array(z.string()),
  totalCount: z.number(),
  nextCursor: z.string().nullable(),
  appliedFilters: z.record(z.string(), z.unknown()),
});

export type AuditorAuditEvidencePageDTO = z.infer<
  typeof AuditorAuditEvidencePageSchema
>;

export const AuditorEvidenceSummaryDTOSchema = z.object({
  totalPackages: z.number(),
  totalFiles: z.number(),
  totalSubmissions: z.number(),
  reviewedSubmissions: z.number(),
  unreviewedSubmissions: z.number(),
  freshness: z.object({
    fresh: z.number(),
    aging: z.number(),
    stale: z.number(),
    unknown: z.number(),
  }),
  sourceBreakdown: z.array(
    z.object({
      type: z.string(),
      count: z.number(),
    }),
  ),
  typeBreakdown: z.array(
    z.object({
      type: z.string(),
      count: z.number(),
    }),
  ),
});

export type AuditorEvidenceSummaryDTO = z.infer<
  typeof AuditorEvidenceSummaryDTOSchema
>;

export const AuditorPolicySummarySchema = z.object({
  submissionId: z.string().nullable(),
  policyDocumentId: z.string(),
  policyDocumentVersionId: z.string(),
  title: z.string(),
  slug: z.string(),
  internalStatus: z.string(),
  currentVersion: z.number(),
  internalApprovedAt: z.date().nullable(),
  internalApprovedBy: z.string().nullable(),
  acknowledgedCount: z.number(),
  pendingCount: z.number(),
  acknowledgmentRate: z.number(),
  verifiedClaimsCount: z.number(),
  linkedControls: z.array(z.string()),
  frameworkRefs: z.array(z.object({ framework: z.string(), refs: z.array(z.string()) })),
  submittedVersion: z.number(),
  submissionStatus: AuditorPolicySubmissionStatusSchema.nullable(),
  submittedAt: z.date().nullable(),
  submittedBy: z.string().nullable(),
  submissionReviewedAt: z.date().nullable(),
  submissionReviewedBy: z.string().nullable(),
  submissionReviewComments: z.string().nullable(),
  latestReviewStatus: AuditorReviewStatusSchema.nullable(),
  commentCount: z.number(),
  unresolvedRequestCount: z.number(),
});

export type AuditorPolicySummaryDTO = z.infer<
  typeof AuditorPolicySummarySchema
>;

export const AuditorPolicySummaryPageSchema = z.object({
  items: z.array(AuditorPolicySummarySchema),
  totalCount: z.number(),
  nextCursor: z.string().nullable(),
  appliedFilters: z.record(z.string(), z.unknown()),
});

export type AuditorPolicySummaryPageDTO = z.infer<
  typeof AuditorPolicySummaryPageSchema
>;

export const AuditorPolicyClaimSnapshotSchema = z.object({
  id: z.string(),
  programId: z.string(),
  policyName: z.string(),
  claimType: z.string().nullable(),
  claimText: z.string(),
  systemOfRecord: z.string(),
  controlRef: z.string().nullable(),
  quoteText: z.string().nullable(),
  docBlockId: z.string().nullable(),
  confidence: z.string().nullable(),
  verification: z.string().nullable(),
  stale: z.boolean(),
  claimTupleKey: z.string().nullable(),
  claimFingerprint: z.string().nullable(),
  generationVersion: z.number(),
  matchConfidence: z.number().nullable(),
  needsReview: z.boolean(),
  disposition: z.string().nullable(),
  dispositionAt: z.string().nullable(),
  dispositionBy: z.string().nullable(),
  dismissReason: z.string().nullable(),
  claimSource: z.string(),
  dismissable: z.boolean(),
  suggestedAction: z.string().nullable(),
  suggestedReason: z.string().nullable(),
  claimTestLinks: z.array(z.object({
    controlEvidenceStatusId: z.string(),
    controlEvidenceStatus: z.object({
      taskCode: z.string().nullable(),
      title: z.string().nullable(),
      template: z.object({ title: z.string().nullable() }).nullish(),
    }).nullish(),
  })),
  createdTestId: z.string().nullable(),
  createdTestStatus: z.string().nullable(),
  createdTestTaskCode: z.string().nullable(),
  createdTestTitle: z.string().nullable(),
  hasCreatedTest: z.boolean(),
  isResolved: z.boolean(),
  createdAt: z.string(),
  templateKey: z.string().nullable(),
  triageStatus: z.string().nullable(),
});

export type AuditorPolicyClaimSnapshot = z.infer<typeof AuditorPolicyClaimSnapshotSchema>;

export const AuditorPolicyDetailSchema = AuditorPolicySummarySchema.extend({
  reviewRecords: z.array(PolicyReviewRecordSchema),
  claimList: z.array(AuditorPolicyClaimSnapshotSchema),
  hasContent: z.boolean(),
  content: z.string().nullable(),
  hasTiptapJson: z.boolean(),
  tiptapJson: z.unknown().nullable(),
  viewMode: z.enum(["inline_markdown", "metadata_only"]),
  sourcePath: z.string().nullable(),
});

export type AuditorPolicyDetailDTO = z.infer<typeof AuditorPolicyDetailSchema>;

/** Person-level rollup of device security checks for the auditor People roster. */
export const AuditorDeviceSecurityCheckStatusSchema = z.enum([
  'PASS',
  'FAIL',
  'MISSING',
  'STALE',
]);

export type AuditorDeviceSecurityCheckStatus = z.infer<
  typeof AuditorDeviceSecurityCheckStatusSchema
>;

export const AuditorPeopleSecurityChecksDTOSchema = z.object({
  disk: AuditorDeviceSecurityCheckStatusSchema,
  firewall: AuditorDeviceSecurityCheckStatusSchema,
  antivirus: AuditorDeviceSecurityCheckStatusSchema,
  autoUpdate: AuditorDeviceSecurityCheckStatusSchema,
  passwords: AuditorDeviceSecurityCheckStatusSchema,
  screenLock: AuditorDeviceSecurityCheckStatusSchema,
  evidence: z.object({
    disk: z.string().nullable(),
    firewall: z.string().nullable(),
    antivirus: z.string().nullable(),
    autoUpdate: z.string().nullable(),
    passwords: z.string().nullable(),
  }),
});

export type AuditorPeopleSecurityChecksDTO = z.infer<
  typeof AuditorPeopleSecurityChecksDTOSchema
>;

export const AuditorPeopleEmployeeDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  department: z.string().nullable(),
  title: z.string().nullable(),
  status: z.string(),
  type: z.string(),
  linked: z.boolean(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  source: z.string(),
  syncedAt: z.coerce.date().nullable(),
  training: z.object({ completed: z.number(), total: z.number() }),
  devices: z.object({ compliant: z.number(), total: z.number() }),
  securityChecks: AuditorPeopleSecurityChecksDTOSchema,
  policyAcknowledgments: z.object({
    acknowledged: z.number(),
    total: z.number(),
  }),
});

export type AuditorPeopleEmployeeDTO = z.infer<
  typeof AuditorPeopleEmployeeDTOSchema
>;

export const AuditorPeopleSummaryDTOSchema = z.object({
  periodStart: z.coerce.date().nullable(),
  periodEnd: z.coerce.date().nullable(),
  asOf: z.coerce.date(),
  employees: z.array(AuditorPeopleEmployeeDTOSchema),
  excluded: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
      department: z.string().nullable(),
      title: z.string().nullable(),
      status: z.string(),
      type: z.string(),
      reason: ComplianceExclusionReasonSchema.nullable(),
      excludedAt: z.coerce.date().nullable(),
    }),
  ),
});

export type AuditorPeopleSummaryDTO = z.infer<
  typeof AuditorPeopleSummaryDTOSchema
>;

export const AuditorPeopleEmployeeDetailsDTOSchema = z.object({
  employee: AuditorPeopleEmployeeDTOSchema,
  relationships: PersonAssetGraphPageDTOSchema,
  profile: z.object({
    manager: z.string().nullable(),
    backgroundCheckStatus: z.string().nullable(),
    backgroundCheckCompletedAt: z.coerce.date().nullable(),
    ndaSignedAt: z.coerce.date().nullable(),
    lifecycleStatus: z.string(),
  }),
  trainingRecords: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
      score: z.number().nullable(),
      completedAt: z.coerce.date().nullable(),
      dueDate: z.coerce.date().nullable(),
      source: z.string().nullable(),
      hasCertificate: z.boolean(),
      certificateUrl: z.string().nullable(),
    }),
  ),
  devices: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.string(),
      manufacturer: z.string().nullable(),
      model: z.string().nullable(),
      osName: z.string().nullable(),
      osVersion: z.string().nullable(),
      complianceStatus: z.string(),
      encryptionEnabled: z.boolean().nullable(),
      firewallEnabled: z.boolean().nullable(),
      antivirusEnabled: z.boolean().nullable(),
      autoUpdateEnabled: z.boolean().nullable(),
      lastCheckIn: z.coerce.date().nullable(),
      mdmProvider: z.string().nullable(),
      mdmMetadata: z.record(z.unknown()).nullable(),
      createdAt: z.coerce.date(),
      lifecycleStatus: DeviceLifecycleStatusSchema,
    }),
  ),
  policies: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      version: z.number(),
      acknowledged: z.boolean(),
      acknowledgedAt: z.coerce.date().nullable(),
      acknowledgedVersion: z.number().nullable(),
    }),
  ),
});

export type AuditorPeopleEmployeeDetailsDTO = z.infer<
  typeof AuditorPeopleEmployeeDetailsDTOSchema
>;

export const AuditorTimelineItemDTOSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  category: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  actorLabel: z.string().nullable().optional(),
  targetLabel: z.string().nullable().optional(),
  deepLink: z.string().nullable().optional(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  createdAt: z.date(),
});

export type AuditorTimelineItemDTO = z.infer<
  typeof AuditorTimelineItemDTOSchema
>;

export const AuditorTimelinePageSchema = z.object({
  items: z.array(AuditorTimelineItemDTOSchema),
  nextCursor: z.string().nullable(),
});

export type AuditorTimelinePageDTO = z.infer<typeof AuditorTimelinePageSchema>;

export const AuditorInvitationSchema = z.object({
  invitationId: z.string(),
  email: z.string(),
  auditCycleId: z.string(),
  auditCycleLabel: z.string(),
  sentAt: z.date(),
  expiresAt: z.date(),
});

export type AuditorInvitation = z.infer<typeof AuditorInvitationSchema>;

export const AuditorMemberSchema = z.object({
  workspaceMemberId: z.string(),
  userId: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  image: z.string().nullable(),
  grantedAt: z.date(),
  grantedBy: z.string(),
});

export type AuditorMember = z.infer<typeof AuditorMemberSchema>;

export const AuditorActivityLogSchema = z.object({
  id: z.string(),
  action: z.string(),
  actionLabel: z.string(),
  category: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  createdAt: z.date(),
  metadata: z.unknown().nullable(),
});

export type AuditorActivityLog = z.infer<typeof AuditorActivityLogSchema>;

// An "open request" is any unresolved auditor comment thread root anchored to
// a CES — a CES stays surfaced as long as it has one. Severity ranks the row
// but does not gate inclusion: EVIDENCE_REQUEST / CLARIFICATION /
// NEEDS_REMEDIATION → action_required, EXCEPTION / EXCEPTION_NOTED →
// attention, plain NOTE / REVIEWED / SATISFACTORY → info.
export const OpenRequestSeveritySchema = z.enum([
  "action_required",
  "attention",
  "info",
]);

export type OpenRequestSeverity = z.infer<typeof OpenRequestSeveritySchema>;

export const OpenRequestDTOSchema = z.object({
  id: z.string(),
  auditCycleId: z.string(),
  // Always TEST or EVIDENCE_ITEM — open requests are anchored to a CES.
  targetType: z.enum(["TEST", "EVIDENCE_ITEM"]),
  // The ControlEvidenceStatus id (test/evidence detail page deep-links here).
  targetId: z.string(),
  targetRef: z.string().nullable(),
  testCode: z.string().nullable(),
  testTitle: z.string().nullable(),
  commentType: AuditorCommentTypeSchema,
  reviewStatus: AuditorReviewStatusSchema.nullable(),
  severity: OpenRequestSeveritySchema,
  // Human-readable verb, e.g. "Requires evidence".
  verb: z.string(),
  // Truncated comment body for the list row.
  content: z.string(),
  isAuditor: z.boolean(),
  authorName: z.string().nullable(),
  replyCount: z.number(),
  createdAt: z.date(),
  // Set when the customer marked the request addressed but the auditor hasn't
  // closed it yet — the thread is still open (resolvedAt null) and stays in the
  // auditor's queue flagged "responded — needs review".
  respondedAt: z.date().nullable(),
  // Null when the thread is still open. Populated when the list was requested
  // with `includeResolved: true` and the root comment has been resolved.
  resolvedAt: z.date().nullable(),
});

export type OpenRequestDTO = z.infer<typeof OpenRequestDTOSchema>;

/**
 * RFC-226 — a PENDING auditor proposal, surfaced as an Open Requests row.
 *
 * Not an `AuditorComment`: it lives on `AuditRequest.proposedTests` and is
 * closed by accept/decline, not by replying and resolving. It rides in the
 * same payload because both are "the auditor is waiting on you" work — the
 * queue is merged, the two objects are not (the Request List page stays the
 * IRL register).
 */
export const OpenRequestProposalDTOSchema = z.object({
  /** `${auditRequestId}:${key}` — stable row id for list selection. */
  id: z.string(),
  auditRequestId: z.string(),
  auditCycleId: z.string(),
  /** The proposal's key within its request — what `resolveProposedTest` takes. */
  key: z.string(),
  /** The parent request, for row context (e.g. "JD-1.5 · Access reviews"). */
  auditorRef: z.string(),
  requestTitle: z.string(),
  title: z.string(),
  /** 'new' materializes a Test on accept; 'modify' edits an existing one;
   *  'scope' activates or defers one. */
  kind: z.enum(["new", "modify", "scope"]),
  /** 'scope' only — which way the auditor asked the scope to move. */
  scopeAction: z.enum(["ACTIVATE", "DEFER"]).nullable().default(null),
  targetCesId: z.string().nullable(),
  targetTaskCode: z.string().nullable(),
  proposedByName: z.string().nullable(),
  proposedAt: z.date().nullable(),
  commentCount: z.number(),
});

export type OpenRequestProposalDTO = z.infer<
  typeof OpenRequestProposalDTOSchema
>;

export const OpenRequestListDTOSchema = z.object({
  items: z.array(OpenRequestDTOSchema),
  total: z.number(),
  /** Pending proposals in the same scope. Empty unless RFC-226 authoring is
   *  in use — an engagement with no proposals sees exactly what it saw before. */
  proposals: z.array(OpenRequestProposalDTOSchema),
});

export type OpenRequestListDTO = z.infer<typeof OpenRequestListDTOSchema>;

// ── Key documents, auditor view ──────────────────────────────────────
// `KeyDocumentsDTO` with each `linkedTestIds` entry resolved to the Test the
// auditor opens, carrying the same review-state inputs the controls tree
// ships — so the dashboard card and the controls page derive one and the same
// badge for a test (`getTestEffectiveStatus` on the web).

export const AuditorKeyDocumentTestSchema = z.object({
  id: z.string(),
  taskCode: z.string().nullable(),
  title: z.string(),
  /** CES status — the fallback scope signal on surfaces without `scopeState`. */
  status: z.string(),
  /** Auditor's latest review verdict on the test (from their comments), if any. */
  reviewStatus: z.string().nullable(),
  hasApprovedSubmission: z.boolean(),
  scopeState: z.enum(["ACTIVE", "DEFERRED", "NOT_APPLICABLE", "ON_HOLD"]),
  deferralKind: z.string().nullable(),
  deactivationReason: z.string().nullable(),
});

export const AuditorKeyDocumentsDTOSchema = KeyDocumentsSchema.extend({
  documents: z.array(
    KeyDocumentSchema.extend({ tests: z.array(AuditorKeyDocumentTestSchema) }),
  ),
});

export type AuditorKeyDocumentTest = z.infer<typeof AuditorKeyDocumentTestSchema>;
export type AuditorKeyDocumentsDTO = z.infer<typeof AuditorKeyDocumentsDTOSchema>;

/** Read-only Procedure execution history exposed to an auditor. Raw execution
 * params, outputs, and step results stay server-side. */
export const AuditorProcedureRunSchema = z.object({
  id: z.string(),
  workflowName: z.string().nullable(),
  definitionVersion: z.number().nullable(),
  status: z.string(),
  triggerType: z.string().nullable(),
  executedAt: z.date(),
  executionTime: z.number(),
  totalSteps: z.number(),
  successfulSteps: z.number(),
  failedSteps: z.number(),
  artifactCount: z.number(),
  actorNames: z.array(z.string()),
  decisionOutcomes: z.array(
    z.object({
      stepTitle: z.string(),
      outcome: z.string(),
      actorName: z.string().nullable(),
    }),
  ),
  artifacts: z.array(
    z.object({
      filename: z.string(),
      mimeType: z.string(),
      url: z.string(),
    }),
  ),
  errorMessage: z.string().nullable(),
});

export type AuditorProcedureRun = z.infer<typeof AuditorProcedureRunSchema>;

export const AuditorProcedureRunHistorySchema = z.object({
  hasProcedureHistory: z.boolean(),
  latestRun: AuditorProcedureRunSchema.nullable(),
  runs: z.array(AuditorProcedureRunSchema),
  truncated: z.boolean(),
});

export type AuditorProcedureRunHistory = z.infer<
  typeof AuditorProcedureRunHistorySchema
>;

// #1865: current program register, not an audit-period population snapshot.
export const AuditorVendorSchema = VendorDTOSchema.pick({
  id: true, displayId: true, name: true, service: true, category: true,
  riskTier: true, residualRiskTier: true, status: true,
  ownerLabel: true, ownerRoleName: true,
  lastReviewDate: true, nextReviewDate: true, offboardedAt: true,
  inherentImpact: true, inherentLikelihood: true, residualImpact: true, residualLikelihood: true,
  customerDataInvolved: true, containsEphi: true, baaStatus: true,
}).extend({
  riskCount: z.number().int().nonnegative(),
  latestAssessment: VendorAssessmentDTOSchema.pick({
    id: true, status: true, version: true, reviewedAt: true,
  }).nullable(),
});
export type AuditorVendor = z.infer<typeof AuditorVendorSchema>;

export const AuditorVendorAssessmentSchema = VendorAssessmentDTOSchema.pick({
  id: true, version: true, status: true, submittedAt: true, completedAt: true,
  reviewedAt: true, reviewerName: true, reviewComments: true,
  securityPosture: true, dataHandling: true, complianceCerts: true,
  contractualReview: true, summary: true, recommendedRiskTier: true,
  fieldProvenance: true, createdAt: true, updatedAt: true, dueAt: true, initiatedByName: true,
}).extend({
  evidenceRequests: z.array(VendorEvidenceRequestDTOSchema),
  inputs: z.array(VendorAssessmentInputDTOSchema),
  controlMappings: z.array(VendorControlMappingDTOSchema),
});
export type AuditorVendorAssessment = z.infer<typeof AuditorVendorAssessmentSchema>;

export const AuditorVendorDetailsSchema = z.object({
  vendor: AuditorVendorSchema.omit({ riskCount: true }).merge(VendorDTOSchema.pick({
    businessCriticality: true, dataProcessingRole: true,
    contactName: true, contactInfo: true, headquarters: true, headquartersCountryCode: true,
    vendorToCompanyAuthMethod: true, companyToVendorAuthMethod: true, environmentAccess: true,
    inherentScore: true, tierRationale: true,
    residualScore: true, residualTierRationale: true,
    onboardedAt: true, contractRenewalDate: true, externalUrl: true,
    baaSignedAt: true, baaExpiresAt: true,
    securityReview: true, contractualTerms: true, notes: true,
  })),
  relationships: z.array(VendorDTOSchema.pick({ id: true, name: true, displayId: true }).extend({
    kind: z.enum(['parent', 'subcontractor']),
  })),
  hasBaaDocument: z.boolean(),
  assessments: z.array(AuditorVendorAssessmentSchema),
  documents: z.array(VendorDocumentDTOSchema),
});
export type AuditorVendorDetails = z.infer<typeof AuditorVendorDetailsSchema>;

/** Read-only risk register row for the Auditor Center. Sync internals, raw
 *  scanner bodies, AI assessment notes and activity stay out. */
export const AuditorRiskSchema = RiskItemDTOSchema.pick({
  id: true, displayId: true, riskType: true, title: true, riskStatement: true,
  affectedSystem: true, category: true, sourceType: true, provider: true,
  likelihood: true, impact: true, riskRating: true,
  residualRisk: true, residualLikelihood: true, residualImpact: true,
  treatment: true, status: true, mitigationPlan: true,
  targetDate: true, reviewDate: true,
  cveIds: true, packageName: true, firstDetectedAt: true, slaDueAt: true, slaState: true,
  createdAt: true, updatedAt: true,
}).extend({ owner: z.string() });
export type AuditorRisk = z.infer<typeof AuditorRiskSchema>;

export const AuditorRiskListFilterSchema = z.object({
  search: z.string().optional(),
  riskRating: RiskRatingEnum.optional(),
  status: RiskStatusEnum.optional(),
  treatment: RiskTreatmentEnum.optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
});

export const AuditorRiskDetailsSchema = AuditorRiskSchema.extend({
  acceptance: RiskAcceptanceDTOSchema.pick({
    justification: true, approverName: true, approvedAt: true, expiresAt: true,
    status: true, revokedAt: true, revokedReason: true,
  }).nullable(),
  linkedTests: z.array(z.object({ cesId: z.string(), displayId: z.string().nullable(), title: z.string(), status: z.string().nullable() })),
  linkedAssets: z.array(z.object({ displayId: z.string().nullable(), name: z.string(), assetType: z.string().nullable(), criticality: z.string().nullable() })),
  linkedVendors: z.array(z.object({ vendorId: z.string(), displayId: z.string().nullable(), name: z.string(), riskTier: z.string().nullable() })),
});
export type AuditorRiskDetails = z.infer<typeof AuditorRiskDetailsSchema>;

export const AuditorAssetSchema = AssetDTOSchema.pick({
  id: true,
  displayId: true,
  name: true,
  assetType: true,
  criticality: true,
  dataClassification: true,
  provider: true,
  environment: true,
  ownerLabel: true,
  ownerRoleName: true,
  technicalOwnerName: true,
  vendorName: true,
  status: true,
  lastReviewedAt: true,
  syncStatus: true,
});
export type AuditorAsset = z.infer<typeof AuditorAssetSchema>;

export const AuditorAssetDetailsSchema = AuditorAssetSchema.merge(
  AssetDTOSchema.pick({
    description: true,
    dataTypes: true,
    containsEphi: true,
    containsPii: true,
    containsPci: true,
    containsIntellectualProperty: true,
    containsEmployeeData: true,
    providerAssetId: true,
    providerAccount: true,
    region: true,
    ownerId: true,
    technicalOwnerId: true,
    vendorId: true,
    discoveredAt: true,
    decommissionedAt: true,
    lastSyncedAt: true,
    parentAssetId: true,
    notes: true,
  }),
);
export type AuditorAssetDetails = z.infer<typeof AuditorAssetDetailsSchema>;
/** Scope facts acknowledged for one Test in one audit cycle, not evidence approval. */
export const AuditTestScopeSnapshotSchema = z.object({
  scopeState: z.enum(["ACTIVE", "DEFERRED", "NOT_APPLICABLE", "ON_HOLD"]),
  deferralKind: z.string().nullable(),
  deactivationReason: z.string().nullable(),
  scopeSource: z.string().nullable(),
  naApprovedAt: z.string().nullable(),
});
export type AuditTestScopeSnapshot = z.infer<typeof AuditTestScopeSnapshotSchema>;
